import { memo, useMemo, useState, useEffect } from "react";
import { Box, Typography, IconButton, Button, Divider, Select, MenuItem, FormControl } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import ViewColumnOutlined from "@mui/icons-material/ViewColumnOutlined";
import AddIcon from "@mui/icons-material/Add";
import toast from "react-hot-toast";
import { getAllFields, columnTypes, getConditionsForType } from "../../../../../hooks/useTableColumnsConfig";
import ConditionRow from "./ConditionRow";
import RemoveConditional from "./RemoveConditional";

const AddConditional = ({
  handleBackOrDiscard,
  onClose,
  conditionals,
  editingConditional,
  onSave,
  onDelete,
}) => {
  const columnHeaders = useMemo(() => getAllFields(), []);
  const EMPTY_CONDITION = {
    condition: "",
    value: "",
    color: "",
  };
  const [selectedColumnType, setSelectedColumnType] = useState("");
  const [selectedColumn, setSelectedColumn] = useState("");
  const [conditions, setConditions] = useState([EMPTY_CONDITION]);
  const NO_VALUE_CONDITIONS = ["is_empty", "is_not_empty", "today", "yesterday", "tomorrow"];

  const isConditionValid = (cond) => {
    if (!cond.condition) return false;
    if (NO_VALUE_CONDITIONS.includes(cond.condition)) return true;
    return !!cond.value;
  };

  useEffect(() => {
    if (editingConditional) {
      setSelectedColumnType(editingConditional.columnType);
      setSelectedColumn(editingConditional.column);
      const conditionsWithColor = editingConditional.conditions.map((cond) => ({
        condition: cond.condition,
        value: cond.value,
        color: cond.color || "",
      }));
      setConditions(conditionsWithColor);
    } else {
      setSelectedColumnType("");
      setSelectedColumn("");
      setConditions([EMPTY_CONDITION]);
    }
  }, [editingConditional]);

  const handleColumnTypeChange = (value) => {
    if (selectedColumn || conditions.some(c => c.condition || c.value)) {
      const confirmed = window.confirm(
        "Changing column type will clear your current selections. Continue?"
      );
      if (!confirmed) return;
    }
    setSelectedColumnType(value);
    setSelectedColumn("");
    setConditions([EMPTY_CONDITION]);
  };

  const handleColumnChange = (value) => {
    setSelectedColumn(value);
  };

  const handleConditionChange = (index, value) => {
    setConditions((prev) => prev.map((cond, i) => {
      if (i === index) {
        return { ...cond, condition: value, value: "" };
      }
      return cond;
    }));
  };

  const handleValueChange = (index, value) => {
    setConditions((prev) => prev.map((cond, i) => (i === index ? { ...cond, value: value } : cond)));
  };

  const handleAddCondition = () => {
    setConditions((prev) => [...prev, { ...EMPTY_CONDITION }]);
  };

  const handleRemoveCondition = (index) => {
    setConditions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleColorChange = (index, value) => {
    setConditions((prev) => prev.map((cond, i) => (i === index ? { ...cond, color: value } : cond)));
  };

  const handleDone = () => {
    if (!selectedColumnType) {
      toast.error("Please select a column type");
      return;
    }
    if (!selectedColumn) {
      toast.error("Please select a column");
      return;
    }
    if (!conditions.some(isConditionValid)) {
      toast.error("Please fill in at least one condition");
      return;
    }
    
    const filteredConditions = conditions
      .filter(isConditionValid)
      .map((cond) => ({
        ...cond,
        color: cond.color || "#ffffff",
      }));
      
    const conditionalData = {
      columnType: selectedColumnType,
      column: selectedColumn,
      conditions: filteredConditions,
    };
    onSave(conditionalData);
  };

  const conditionOptions = useMemo(() => {
    return getConditionsForType(selectedColumnType);
  }, [selectedColumnType]);

  const filteredColumns = useMemo(() => {
    if (!selectedColumnType) return [];
    const columnsWithRules = conditionals.map((c) => c.column);
    return columnHeaders.filter((col) => {
      const matchesType = col.type === selectedColumnType;
      const isCurrentColumn = editingConditional && col.key === editingConditional.column;
      return matchesType && (!columnsWithRules.includes(col.key) || isCurrentColumn);
    });
  }, [selectedColumnType, columnHeaders, conditionals, editingConditional]);

  const columnName = useMemo(() => {
    if (!selectedColumn) return "column";
    const column = columnHeaders.find((col) => col.key === selectedColumn);
    return column ? column.label.toLowerCase() : selectedColumn;
  }, [selectedColumn, columnHeaders]);

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2 }}>
        {conditionals.length > 0 ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton onClick={handleBackOrDiscard} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6">Back</Typography>
          </Box>
        ) : (
          <Box />
        )}
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />

      <Box sx={{ flexGrow: 1, overflow: "auto", px: 2, py: 3 }}>
        <FormControl fullWidth size="small" sx={{ mb: 3 }}>
          <Typography sx={{ mb: 1, color: "text.secondary" }}>Select column type</Typography>
          <Select value={selectedColumnType} onChange={(e) => handleColumnTypeChange(e.target.value)} displayEmpty>
            <MenuItem value="" disabled>
              None
            </MenuItem>
            {columnTypes.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedColumnType && (
          <FormControl fullWidth size="small" sx={{ mb: 3 }}>
            <Typography sx={{ mb: 1, color: "text.secondary" }}>Select column</Typography>
            <Select
              value={selectedColumn}
              onChange={(e) => handleColumnChange(e.target.value)}
              displayEmpty
              IconComponent={ViewColumnOutlined}
            >
              <MenuItem value="" disabled>
                None
              </MenuItem>
              {filteredColumns.map((column) => (
                <MenuItem key={column.key} value={column.key}>
                  {column.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {selectedColumn && (
          <>
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <Typography sx={{ flex: 1, color: "text.secondary" }}>Select condition</Typography>
              <Typography sx={{ flex: 1, color: "text.secondary" }}>Add value</Typography>
              <Box sx={{ width: 40, flexShrink: 0 }} />
              <Box sx={{ width: 40, flexShrink: 0 }} />
            </Box>

            {conditions.map((conditionItem, index) => (
              <ConditionRow
                key={`condition-${index}-${conditionItem.condition}-${conditionItem.value}`}
                {...{
                  conditionItem,
                  conditionOptions,
                  handleConditionChange,
                  handleValueChange,
                  handleColorChange,
                  handleRemoveCondition,
                  index,
                  selectedColumnType,
                }}
              />
            ))}

            <Button
              fullWidth
              variant="text"
              startIcon={<AddIcon />}
              onClick={handleAddCondition}
              sx={{
                mt: 2,
                justifyContent: "flex-start",
              }}
            >
              Add more condition
            </Button>

            <RemoveConditional
              {...{
                editingConditional,
                onDelete,
                columnName,
              }}
            />
          </>
        )}
      </Box>

      <Divider />

      <Box sx={{ p: 2, display: "flex", gap: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={handleBackOrDiscard}
        >
          Discard
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={handleDone}
          disableElevation
        >
          {editingConditional ? "Update" : "Done"}
        </Button>
      </Box>
    </>
  );
};

export default memo(AddConditional);
